import {
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  InputLabel,
  CircularProgress,
  Paper,
  styled,
} from '@mui/material';
import { Stack } from '@mui/system';
import { useDispatch } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import {
  setAccessToken,
  setCompanyColor,
  setIsManualAppointmentBlock,
  setIsSingleCalendarManagement,
  setLoginData,
  setRefreshToken,
  setSoftware,
  setVisibilityUserEnum,
} from '../../store/root';
import axios from 'axios';
import { BluLoader } from '@bludata/components';
import { useEffect, useState } from 'react';
import {
  getRequestInterceptorId,
  getResponseInterceptorId,
} from '../../lib/interceptors';
import { setCookie } from '../../lib/cookie';
import md5 from 'md5';
import BluBookingLogo from '../../assets/BluBookingWhite.png';

export interface LoginForm {
  identificationCode: string;
  username: string;
  password: string;
}

const Background = styled(Stack)(() => ({
  display: 'flex',
  height: '100vh',
  width: '100vw',
  position: 'relative',
  overflow: 'hidden',

  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    backgroundImage: `linear-gradient(90deg, rgba(255, 255, 255, 0) 40%, rgba(22, 65, 148, 1) 100%), url("${
      import.meta.env.BASE_URL
    }background-blu-booking.jpg")`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    // filter: 'blur(2px)',
    zIndex: -1,
  },
}));

const AuthContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  flexWrap: 'wrap',
  alignItems: 'center',
  width: '100%',
  minWidth: '288px',
  maxWidth: '400px',
  padding: '40px 30px',
  gap: '1rem',
  marginLeft: 'auto',
  marginRight: '20%',
  marginTop: 'auto',
  marginBottom: 'auto',
  borderRadius: '0.5rem',
  backgroundColor: '#fff',
  color: '#333',
  boxShadow:
    'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
  '@media only screen and (max-width: 1500px)': {
    marginRight: '10%',
  },
}));

const Login = () => {
  const dispatch = useDispatch();

  const [badLogin, setBadLogin] = useState('');
  const [loading, setLoading] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  // const md5 = require('md5');

  axios.defaults.baseURL = import.meta.env.VITE_NX_PUBLIC_API_URL;
  axios.defaults.headers.common['Authorization'] = undefined;
  const urlParams = new URLSearchParams(window.location.search);

  const requestInterceptorId = getRequestInterceptorId();
  const responseInterceptorId = getResponseInterceptorId();
  if (requestInterceptorId)
    axios.interceptors.request.eject(requestInterceptorId);
  if (responseInterceptorId)
    axios.interceptors.response.eject(responseInterceptorId);

  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
  } = useForm({
    defaultValues: {
      username: localStorage.getItem('BluBookingUsername') || '',
      identificationCode:
        localStorage.getItem('BluBookingIdentificationCode') || '',
      password: '',
    },
  });

  useEffect(() => {
    if (urlParams.has('identificationCode')) {
      setValue('identificationCode', urlParams.get('identificationCode') || '');
    }
    if (urlParams.has('username')) {
      setValue('username', urlParams.get('username') || '');
    }
  });

  const onSubmit = (data: LoginForm) => {
    /*

      identificationCode = 1595A-18B4LI
      Username = Pierantonio
      Pass = 12345678

    */
    setLoading(true);

    axios
      .post('/api/authenticate/login', {
        identificationCode: data.identificationCode.trim().toUpperCase(),
        username: data.username.trim(),
        password: md5(data.password),
      })
      .then((res: any) => {
        localStorage.setItem(
          'BluBookingIdentificationCode',
          data.identificationCode.trim().toUpperCase()
        );
        localStorage.setItem('BluBookingUsername', data.username);

        dispatch(setAccessToken(res.data.data.token));
        setCookie('BluBookingToken', res.data.data.token);

        dispatch(setRefreshToken(res.data.data.refreshToken));
        setCookie('BluBookingRefreshToken', res.data.data.refreshToken, 48);

        dispatch(setCompanyColor(res.data.data.companyColorEnum));
        setCookie('BluBookingCompanyColor', res.data.data.companyColorEnum);

        if (res.data.data.photo !== null)
          setCookie('BluBookingUserPhoto', res.data.data.photo);
        setCookie('BluBookingUserAdmin', res.data.data.isAdmin);

        setCookie('BluBookingSoftware', res.data.data.software);

        if (
          res.data.data?.isManualAppointmentBlock !== null &&
          res.data.data?.isManualAppointmentBlock !== undefined
        ) {
          setCookie(
            'BluBookingManualAppointmentBlock',
            res.data.data?.isManualAppointmentBlock ?? false
          );
        }

        if (res.data.data?.webSocketChannel)
          setCookie('BluBookingSocketChannel', res.data.data?.webSocketChannel);
        setCookie('BluBookingpointOfSaleId', res?.data?.data?.pointOfSaleId);

        setCookie(
          'BluBookingIsManualAppointmentBlock',
          res?.data?.data?.isManualAppointmentBlock ?? false
        );
        dispatch(
          setIsManualAppointmentBlock(
            res?.data?.data?.isManualAppointmentBlock ?? false
          )
        );

        setCookie(
          'BluBookingIsSingleCalendarManagement',
          res?.data?.data?.isSingleCalendarManagement ?? true
        );
        dispatch(
          setIsSingleCalendarManagement(
            res?.data?.data?.isSingleCalendarManagement ?? true
          )
        );

        dispatch(setSoftware(res.data.data.software));
        dispatch(setVisibilityUserEnum(res.data.data?.visibilityUserEnum ?? 0));
        setCookie(
          'BluBookingVisibilityUserEnum',
          res?.data?.data?.visibilityUserEnum ?? 0
        );

        dispatch(
          setLoginData({
            photo: res.data.data.photo,
            isAdmin: res.data.data.isAdmin,
            username: res.data.data.username,
            pointOfSaleId: res?.data?.data?.pointOfSaleId,
            socketChannel: res.data.data?.webSocketChannel,
          })
        );
      })
      .catch((err) => {
        setBadLogin('Codice identificativo, utente o password errati');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Background>
      <BluLoader open={loading} />
      <Stack flex={1} overflow="auto" justifyItems="end">
        <AuthContainer>
          <Stack flex={1} sx={{ margin: '0 15px' }}>
            <img
              src={BluBookingLogo}
              alt="Blu Booking"
              height={30}
              style={{ marginBottom: '40px' }}
            />
            <Typography
              component="h1"
              variant="h5"
              fontWeight={500}
              textAlign="center"
              sx={{ marginBottom: '20px', fontSize: '20px', fontWeight: '400' }}
            >
              Inserisci le tue credenziali
            </Typography>
            {/* <Typography component="h1" variant="h5" fontWeight={500}>
            Console Blu Booking
          </Typography> */}
            {badLogin && (
              <Alert
                sx={{ width: '300px', marginBottom: '1rem' }}
                severity="error"
              >
                {badLogin}
              </Alert>
            )}
            <Stack
              width="100%"
              gap="18px"
              component="form"
              onSubmit={handleSubmit(onSubmit)}
            >
              <Controller
                name="identificationCode"
                rules={{ required: true }}
                control={control}
                render={({ field }) => {
                  const hasError = errors.identificationCode;
                  return (
                    <Stack flex="1">
                      <InputLabel
                        sx={{ color: '#164194 !important', fontWeight: '400' }}
                      >
                        Codice identificativo
                      </InputLabel>
                      {hasError && (
                        <InputLabel sx={{ color: '#b00 !important' }}>
                          Codice identificativo è obbligatorio
                        </InputLabel>
                      )}
                      <TextField
                        // error={!!hasError}
                        variant="standard"
                        autoComplete="identificationCode"
                        {...field}
                        inputProps={{
                          style: { textTransform: 'uppercase' },
                        }}
                        sx={{
                          '& .MuiInputBase-root': {
                            height: '38px',
                            border: 'none',
                            borderRadius: 0,
                            '&:before': {
                              opacity: 1,
                              borderBottom: '1px solid #92aed8',
                            },
                            '&:hover:before': {
                              borderBottom: '1px solid #92aed8 !important',
                            },
                            '&:after': {
                              borderBottom: '2px solid #164194',
                            },
                          },
                        }}
                      />
                    </Stack>
                  );
                }}
              />
              <Controller
                name="username"
                rules={{ required: true }}
                control={control}
                render={({ field }) => {
                  const hasError = errors.username;
                  return (
                    <Stack flex="1">
                      <InputLabel
                        sx={{ color: '#164194 !important', fontWeight: '400' }}
                      >
                        Utente
                      </InputLabel>
                      {hasError && (
                        <InputLabel sx={{ color: '#b00 !important' }}>
                          Utente è obbligatorio
                        </InputLabel>
                      )}
                      <TextField
                        error={!!hasError}
                        variant="standard"
                        autoComplete="username"
                        {...field}
                        sx={{
                          '& .MuiInputBase-root': {
                            height: '38px',
                            border: 'none',
                            borderRadius: 0,
                            '&:before': {
                              opacity: 1,
                              borderBottom: '1px solid #92aed8',
                            },
                            '&:hover:before': {
                              borderBottom: '1px solid #92aed8 !important',
                            },
                            '&:after': {
                              borderBottom: '2px solid #164194',
                            },
                          },
                        }}
                      />
                    </Stack>
                  );
                }}
              />
              <Controller
                name="password"
                rules={{ required: true }}
                control={control}
                render={({ field }) => {
                  const hasError = errors.password;
                  return (
                    <Stack flex="1">
                      <InputLabel
                        sx={{ color: '#164194 !important', fontWeight: '400' }}
                      >
                        Password
                      </InputLabel>
                      {hasError && (
                        <InputLabel sx={{ color: '#b00 !important' }}>
                          Password è obbligatoria
                        </InputLabel>
                      )}
                      <TextField
                        error={!!hasError}
                        variant="standard"
                        type="password"
                        autoComplete="current-password"
                        {...field}
                        sx={{
                          '& .MuiInputBase-root': {
                            height: '38px',
                            border: 'none',
                            borderRadius: 0,
                            '&:before': {
                              opacity: 1,
                              borderBottom: '1px solid #92aed8',
                            },
                            '&:hover:before': {
                              borderBottom: '1px solid #92aed8 !important',
                            },
                            '&:after': {
                              borderBottom: '2px solid #164194',
                            },
                          },
                        }}
                      />
                    </Stack>
                  );
                }}
              />
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                sx={{
                  width: '100px',
                  height: '40px',
                  borderRadius: '20px',
                  alignSelf: 'end',
                  backgroundColor: '#164194',
                  '&:hover': {
                    backgroundColor: '#1662c7',
                    borderColor: '#204d74',
                  },
                  marginTop: '20px',
                  textAlign: 'center',
                }}
              >
                <Typography sx={{ width: '100%', color: 'inherit' }}>
                  Accedi
                </Typography>
              </Button>
            </Stack>
          </Stack>
        </AuthContainer>
      </Stack>
      <Paper
        sx={{
          px: 0.5,
          borderRadius: '0px',
          zIndex: 5,
          opacity: 0.7,
          boxShadow:
            '2px 2px 5px 0 rgba(0, 0, 0, 0.12), 2px 2px 5px 0 rgba(0, 0, 0, 0.08)',
        }}
      >
        <Stack
          flexDirection="row"
          justifyContent="end"
          alignItems="center"
          sx={{ height: '30px' }}
        >
          <Typography
            variant="body2"
            sx={{
              padding: '10px 20px',
              lineHeight: '20px',
              fontSize: '16px',
              fontFamily: `Roboto, "Helvetica Neue",Helvetica,Arial,sans-serif !important`,
            }}
          >
            © {new Date().getFullYear()} Bludata S.r.l.
          </Typography>
          <a href="https://www.bludata.com" target="_blank" rel="noreferrer">
            <Stack flexDirection="column" justifyContent="center">
              <img
                style={{
                  height: '16px',
                  margin: '5px',
                  marginRight: '15px',
                  cursor: 'pointer',
                }}
                alt="Bludata S.r.l."
                src={`https://focus-dev.bludata.cloud/b2ctemplates/shared/logo.png`}
              />
            </Stack>
          </a>
        </Stack>
      </Paper>
    </Background>
  );
};

export default Login;
